/**
 * Copyright (c) 2018-present, Didi, Inc.
 * All rights reserved.
 *
 * @author Cory(kuanghongrui@didichuxing.com)
 *
 * @file The class of ElementUtils
 */

import { IElementExtraAttrs } from 'com/didichuxing/tracker/h5/plugins/utils/IElementExtraAttrs';
import { IViewportExtraAttrs } from 'com/didichuxing/tracker/h5/plugins/utils/IViewportExtraAttrs';
import { IObjectMap } from 'com/didichuxing/IObjectMap';
import ViewportUtils from 'com/didichuxing/tracker/h5/plugins/utils/ViewportUtils';

export default abstract class ElementUtils {

    /**
     * 获取目标对象所在的层级
     * 例如：body->0->1->3
     * @returns {string}
     */
    public static getHierarchy(element: Element): string {
        const hierarchies: Array<number | string> = [];
        while (element && element.tagName.toLowerCase() !== 'body') {
            const index: number = ElementUtils.getNodeIndexInSiblings(element);
            if (index >= 0) {
                hierarchies.push(index);
            }
            element = element.parentElement as Element;
        }
        if (element) {
            hierarchies.push('body');
        } else {
            hierarchies.push('document');
        }
        return hierarchies.reverse().join('->');
    }

    /**
     * 获取当前节点在其兄弟节点中的索引值。
     * @param {Element} element
     * @returns {number}
     */
    private static getNodeIndexInSiblings(element: Element): number {
        const parent: Element = element.parentElement as Element;
        if (parent) {
            const children: HTMLCollection = parent.children;
            for (let i: number = 0, j: number = children.length; i < j; ++i) {
                if (element === children[i]) {
                    return i;
                }
            }
        }
        return -1;
    }

    /**
     * 获取TargetTagName
     * 在IE下 svg 的tagName可能为空 所以需要判断
     * 对于元素节点来说,tagName属性的值和nodeName属性的值是相同的
     * 所以如果tagName没有就去拿nodeName
     * 都没有就返回空字符串
     * @param element
     * @return string
     */
    private static getTargetTagName(element: Element): string {
        if (!element) {
            return '';
        }
        if (element.tagName && element.tagName.toLocaleUpperCase) {
            return element.tagName.toLocaleUpperCase();
        } else if (element.nodeName && element.nodeName.toLocaleLowerCase) {
            return element.nodeName.toLocaleLowerCase();
        } else {
            return '';
        }
    }

    /**
     * 获取Element额外的属性。
     * @param {HTMLElement} element
     * @returns {IElementExtraAttrs}
     */
    public static getElementExtraAttrs(element: HTMLElement): IElementExtraAttrs | IViewportExtraAttrs {
        const targetRect: ClientRect = element.getBoundingClientRect();
        return {
            targetTagName: this.getTargetTagName(element),
            targetId: element.id,
            targetName: element.getAttribute('name') || '',
            targetHref: element.getAttribute('href') || '',
            targetClassName: element.className,
            targetX: Math.round(targetRect.left),
            targetY: Math.round(targetRect.top),
            targetWidth: Math.round(targetRect.width) || element.offsetWidth,
            targetHeight: Math.round(targetRect.height) || element.offsetHeight,
            hierarchy: element.id || element.getAttribute('name') || ElementUtils.getHierarchy(element),
            ...ViewportUtils.getViewportBox()
        };
    }

    /**
     * 获取Element额外属性映射KV值。
     * @returns {IObjectMap<string>}
     */
    public static getElementExtraAttrsMap(): IObjectMap<string> {
        return {
            targetTagName: 'l',
            targetId: 'id',
            targetName: 'name',
            targetHref: 'href',
            targetText: 'text',
            targetClassName: 'class',
            targetX: 'ex',
            targetY: 'ey',
            targetWidth: 'ew',
            targetHeight: 'eh',
            hierarchy: 'rn',
            ...ViewportUtils.getViewportBoxAttrsMap()
        };
    }
}
